import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import {
  AlertCircle, Check, Loader2, Plus, Trash2, ChevronUp, ChevronDown,
  ArrowLeft, Type, Image as ImageIcon, ListChecks, LayoutTemplate, ListOrdered, LayoutGrid,
} from "lucide-react";
import * as adminApi from "@/api/admin";
import { QuestionnaireFieldEditor, IconButton } from "./QuestionnaireFieldEditor";
import {
  type SectionDraft, type QuestionnaireFieldDraft, type StepDraft, type CardDraft, type SectionType,
} from "./types";

interface LandingPageEditorViewProps {
  slug: string;
  onBack: () => void;
  // Optional: lets the parent (e.g. breadcrumbs, the "landing-page-editor" view
  // route state) stay in sync when the slug is renamed from inside the editor.
  onSlugChange?: (newSlug: string) => void;
}

function makeId() {
  return Math.random().toString(36).slice(2);
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// Extend this list as new campaigns get a backend handler (see
// api.services.<campaign>_campaign). The value here must match the
// campaign's registered integration code exactly.
const CAMPAIGN_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "None — plain questionnaire" },
  { value: "unistudents", label: "Unistudents collab" },
  { value: "dimos_athinaion", label: "Dimos Athinaion collab" },
];

// Backend sections/fields/steps only carry a real `id`, never a `clientId` —
// this assigns one at every level so React can key and diff each item
// independently. Without this, every loaded item's clientId is `undefined`,
// and update handlers' `x.clientId === clientId` checks match ALL of them at
// once, making every field's/step's edits change together.
function withClientIds(sections: Omit<SectionDraft, "clientId" | "questionnaire_fields" | "steps" | "cards"> & {
  questionnaire_fields: Omit<QuestionnaireFieldDraft, "clientId">[];
  steps?: Omit<StepDraft, "clientId">[];
  cards?: Omit<CardDraft, "clientId">[];
  [key: string]: unknown;
}[]): SectionDraft[] {
  return sections.map((s) => ({
    ...(s as SectionDraft),
    clientId: makeId(),
    questionnaire_fields: s.questionnaire_fields.map((f) => ({
      ...(f as QuestionnaireFieldDraft),
      clientId: makeId(),
    })),
    steps: (s.steps ?? []).map((st) => ({
      ...(st as StepDraft),
      clientId: makeId(),
    })),
    cards: (s.cards ?? []).map((c) => ({
      ...(c as CardDraft),
      clientId: makeId(),
    })),
  }));
}

function emptyField(order: number): QuestionnaireFieldDraft {
  return {
    clientId: makeId(),
    field_type: "email",
    label: "",
    required: true,
    options: [],
    order,
  };
}

function emptyStep(order: number): StepDraft {
  return {
    clientId: makeId(),
    text: "",
    order,
    icon_url: null,
  };
}

function emptyCard(order: number): CardDraft {
  return {
    clientId: makeId(),
    title: "",
    description: "",
    link_text: "",
    link_url: "",
    order,
    icon_url: null,
  };
}

function emptySection(order: number, type: SectionType): SectionDraft {
  return {
    clientId: makeId(),
    section_type: type,
    order,
    heading: "",
    body: "",
    image_url: null,
    button_text: "",
    button_url: "",
    integration_code: "",
    questionnaire_fields: type === "questionnaire" ? [emptyField(0)] : [],
    steps: type === "steps" ? [emptyStep(0), emptyStep(1), emptyStep(2)] : [],
    cards: type === "cards" ? [emptyCard(0), emptyCard(1), emptyCard(2)] : [],
  };
}

function reorder<T extends { order: number }>(list: T[]): T[] {
  return list.map((item, i) => ({ ...item, order: i }));
}

function move<T>(list: T[], index: number, dir: -1 | 1): T[] {
  const next = [...list];
  const target = index + dir;
  if (target < 0 || target >= next.length) return list;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest whitespace-nowrap">
        {children}
      </span>
      <Separator className="flex-1" />
    </div>
  );
}

function StepEditor({
  step,
  index,
  onChange,
  onRemove,
  onMove,
  isFirst,
  isLast,
  onUploadIcon,
}: {
  step: StepDraft;
  index: number;
  onChange: (step: StepDraft) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  isFirst: boolean;
  isLast: boolean;
  onUploadIcon: (file: File) => void;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-md p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
          Step {index + 1}
        </span>
        <div className="flex items-center gap-1">
          <IconButton title="Move step up" disabled={isFirst} onClick={() => onMove(-1)}>
            <ChevronUp size={14} />
          </IconButton>
          <IconButton title="Move step down" disabled={isLast} onClick={() => onMove(1)}>
            <ChevronDown size={14} />
          </IconButton>
          <IconButton title="Remove step" variant="danger" onClick={onRemove}>
            <Trash2 size={14} />
          </IconButton>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <div className="shrink-0 space-y-1.5">
          {step.icon_url ? (
            <img
              src={step.icon_url}
              alt=""
              className="w-12 h-12 object-contain rounded-full border border-gray-200 bg-gray-50 p-1.5"
            />
          ) : (
            <div className="w-12 h-12 rounded-full border border-dashed border-gray-300 bg-gray-50" />
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUploadIcon(file);
            }}
            className="text-[11px] text-gray-500 w-24 file:mr-1"
          />
        </div>
        <div className="flex-1 space-y-1">
          <label className="text-xs font-medium text-gray-600">Text</label>
          <Textarea
            value={step.text}
            onChange={(e) => onChange({ ...step, text: e.target.value })}
            placeholder="Εγγράφεστε δωρεάν στο PADU app"
            rows={2}
          />
        </div>
      </div>
      {!step.id && (
        <p className="text-[11px] text-gray-400">
          Save the page once first, then upload an icon for this step.
        </p>
      )}
    </div>
  );
}

function CardEditor({
  card,
  index,
  onChange,
  onRemove,
  onMove,
  isFirst,
  isLast,
  onUploadIcon,
}: {
  card: CardDraft;
  index: number;
  onChange: (card: CardDraft) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  isFirst: boolean;
  isLast: boolean;
  onUploadIcon: (file: File) => void;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-md p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
          Card {index + 1}
        </span>
        <div className="flex items-center gap-1">
          <IconButton title="Move card up" disabled={isFirst} onClick={() => onMove(-1)}>
            <ChevronUp size={14} />
          </IconButton>
          <IconButton title="Move card down" disabled={isLast} onClick={() => onMove(1)}>
            <ChevronDown size={14} />
          </IconButton>
          <IconButton title="Remove card" variant="danger" onClick={onRemove}>
            <Trash2 size={14} />
          </IconButton>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <div className="shrink-0 space-y-1.5">
          {card.icon_url ? (
            <img
              src={card.icon_url}
              alt=""
              className="w-12 h-12 object-contain rounded-xl border border-gray-200 bg-gray-50 p-1.5"
            />
          ) : (
            <div className="w-12 h-12 rounded-xl border border-dashed border-gray-300 bg-gray-50" />
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUploadIcon(file);
            }}
            className="text-[11px] text-gray-500 w-24 file:mr-1"
          />
        </div>
        <div className="flex-1 space-y-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Title</label>
            <Input
              value={card.title}
              onChange={(e) => onChange({ ...card, title: e.target.value })}
              placeholder="Ανταμοιβή σε κάθε αγορά"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Description</label>
            <Textarea
              value={card.description}
              onChange={(e) => onChange({ ...card, description: e.target.value })}
              placeholder="Κέρδισε PADU points και δωροεπιταγές…"
              rows={2}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">Link text</label>
          <Input
            value={card.link_text}
            onChange={(e) => onChange({ ...card, link_text: e.target.value })}
            placeholder="Μάθε περισσότερα"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">Link URL</label>
          <Input
            value={card.link_url}
            onChange={(e) => onChange({ ...card, link_url: e.target.value })}
            placeholder="/antamoivi-se-kathe-agora"
          />
        </div>
      </div>

      {!card.id && (
        <p className="text-[11px] text-gray-400">
          Save the page once first, then upload an icon for this card.
        </p>
      )}
    </div>
  );
}

function SectionEditor({
  section,
  onChange,
  onRemove,
  onMove,
  isFirst,
  isLast,
  onUploadImage,
  onUploadStepIcon,
  onUploadCardIcon,
}: {
  section: SectionDraft;
  onChange: (section: SectionDraft) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  isFirst: boolean;
  isLast: boolean;
  onUploadImage: (file: File) => void;
  onUploadStepIcon: (step: StepDraft, file: File) => void;
  onUploadCardIcon: (card: CardDraft, file: File) => void;
}) {
  function updateField(clientId: string, updated: QuestionnaireFieldDraft) {
    onChange({
      ...section,
      questionnaire_fields: section.questionnaire_fields.map((f) =>
        f.clientId === clientId ? updated : f
      ),
    });
  }

  function removeField(clientId: string) {
    onChange({
      ...section,
      questionnaire_fields: reorder(
        section.questionnaire_fields.filter((f) => f.clientId !== clientId)
      ),
    });
  }

  function moveField(index: number, dir: -1 | 1) {
    onChange({
      ...section,
      questionnaire_fields: reorder(move(section.questionnaire_fields, index, dir)),
    });
  }

  function addField() {
    onChange({
      ...section,
      questionnaire_fields: [
        ...section.questionnaire_fields,
        emptyField(section.questionnaire_fields.length),
      ],
    });
  }

  function updateStep(clientId: string, updated: StepDraft) {
    onChange({
      ...section,
      steps: section.steps.map((s) => (s.clientId === clientId ? updated : s)),
    });
  }

  function removeStep(clientId: string) {
    onChange({
      ...section,
      steps: reorder(section.steps.filter((s) => s.clientId !== clientId)),
    });
  }

  function moveStep(index: number, dir: -1 | 1) {
    onChange({
      ...section,
      steps: reorder(move(section.steps, index, dir)),
    });
  }

  function addStep() {
    onChange({
      ...section,
      steps: [...section.steps, emptyStep(section.steps.length)],
    });
  }

  function updateCard(clientId: string, updated: CardDraft) {
    onChange({
      ...section,
      cards: section.cards.map((c) => (c.clientId === clientId ? updated : c)),
    });
  }

  function removeCard(clientId: string) {
    onChange({
      ...section,
      cards: reorder(section.cards.filter((c) => c.clientId !== clientId)),
    });
  }

  function moveCard(index: number, dir: -1 | 1) {
    onChange({
      ...section,
      cards: reorder(move(section.cards, index, dir)),
    });
  }

  function addCard() {
    onChange({
      ...section,
      cards: [...section.cards, emptyCard(section.cards.length)],
    });
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
          {section.section_type} section
        </span>
        <div className="flex items-center gap-1">
          <IconButton title="Move section up" disabled={isFirst} onClick={() => onMove(-1)}>
            <ChevronUp size={14} />
          </IconButton>
          <IconButton title="Move section down" disabled={isLast} onClick={() => onMove(1)}>
            <ChevronDown size={14} />
          </IconButton>
          <IconButton title="Remove section" variant="danger" onClick={onRemove}>
            <Trash2 size={14} />
          </IconButton>
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-600">Heading</label>
        <Input
          value={section.heading}
          onChange={(e) => onChange({ ...section, heading: e.target.value })}
          placeholder="Section heading (optional)"
        />
      </div>

      {(section.section_type === "text" || section.section_type === "hero") && (
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">
            {section.section_type === "hero" ? "Body / subtext" : "Body"}
          </label>
          <Textarea
            value={section.body}
            onChange={(e) => onChange({ ...section, body: e.target.value })}
            placeholder="Section text…"
            rows={4}
          />
        </div>
      )}

      {(section.section_type === "image" || section.section_type === "hero") && (
        <div className="space-y-2">
          {section.image_url && (
            <img src={section.image_url} alt="" className="max-h-40 rounded-md border border-gray-200" />
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUploadImage(file);
            }}
            className="text-sm text-gray-600"
          />
          <p className="text-[11px] text-gray-400">
            {section.id
              ? "Uploads immediately."
              : "Save the page once first, then upload an image for this section."}
          </p>
        </div>
      )}

      {section.section_type === "hero" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Button text</label>
            <Input
              value={section.button_text}
              onChange={(e) => onChange({ ...section, button_text: e.target.value })}
              placeholder="ΔΩΡΕΑΝ ΕΓΓΡΑΦΗ"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Button URL</label>
            <Input
              value={section.button_url}
              onChange={(e) => onChange({ ...section, button_url: e.target.value })}
              placeholder="https://…"
            />
          </div>
        </div>
      )}

      {section.section_type === "questionnaire" && (
        <div className="space-y-3">
          <div className="space-y-1.5 bg-amber-50 border border-amber-200 rounded-md p-3">
            <label className="text-xs font-medium text-gray-700">Campaign integration</label>
            <select
              value={section.integration_code || ""}
              onChange={(e) => onChange({ ...section, integration_code: e.target.value })}
              className="w-full h-9 rounded-md border border-amber-300 bg-white px-3 text-sm text-gray-700"
            >
              {CAMPAIGN_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {section.integration_code && (
              <p className="text-[11px] text-gray-600">
                Submissions on the public page will run through this campaign's
                registration/award flow instead of being stored as plain answers.
                Add a single <strong>Phone</strong> field below for the mobile number input.
              </p>
            )}
          </div>

          <SectionHeading>Fields</SectionHeading>
          {section.questionnaire_fields.length === 0 && (
            <p className="text-xs text-gray-400">No fields yet — add one below.</p>
          )}
          <div className="space-y-3 bg-gray-50 border border-gray-100 rounded-md p-3">
            {section.questionnaire_fields.map((field, i) => (
              <QuestionnaireFieldEditor
                key={field.clientId}
                field={field}
                onChange={(updated) => updateField(field.clientId, updated)}
                onRemove={() => removeField(field.clientId)}
                onMove={(dir) => moveField(i, dir)}
                isFirst={i === 0}
                isLast={i === section.questionnaire_fields.length - 1}
              />
            ))}
            {section.questionnaire_fields.length === 0 && (
              <div className="h-1" />
            )}
            <Button
              type="button"
              onClick={addField}
              className="bg-gray-800 hover:bg-gray-900 text-white text-sm h-9"
            >
              <Plus size={14} className="mr-1.5" /> Add field
            </Button>
          </div>
        </div>
      )}

      {section.section_type === "steps" && (
        <div className="space-y-3">
          <SectionHeading>Steps</SectionHeading>
          {section.steps.length === 0 && (
            <p className="text-xs text-gray-400">No steps yet — add one below.</p>
          )}
          <div className="space-y-3 bg-gray-50 border border-gray-100 rounded-md p-3">
            {section.steps.map((step, i) => (
              <StepEditor
                key={step.clientId}
                step={step}
                index={i}
                onChange={(updated) => updateStep(step.clientId, updated)}
                onRemove={() => removeStep(step.clientId)}
                onMove={(dir) => moveStep(i, dir)}
                isFirst={i === 0}
                isLast={i === section.steps.length - 1}
                onUploadIcon={(file) => onUploadStepIcon(step, file)}
              />
            ))}
            <Button
              type="button"
              onClick={addStep}
              className="bg-gray-800 hover:bg-gray-900 text-white text-sm h-9"
            >
              <Plus size={14} className="mr-1.5" /> Add step
            </Button>
          </div>
        </div>
      )}

      {section.section_type === "cards" && (
        <div className="space-y-3">
          <SectionHeading>Cards</SectionHeading>
          {section.cards.length === 0 && (
            <p className="text-xs text-gray-400">No cards yet — add one below.</p>
          )}
          <div className="space-y-3 bg-gray-50 border border-gray-100 rounded-md p-3">
            {section.cards.map((card, i) => (
              <CardEditor
                key={card.clientId}
                card={card}
                index={i}
                onChange={(updated) => updateCard(card.clientId, updated)}
                onRemove={() => removeCard(card.clientId)}
                onMove={(dir) => moveCard(i, dir)}
                isFirst={i === 0}
                isLast={i === section.cards.length - 1}
                onUploadIcon={(file) => onUploadCardIcon(card, file)}
              />
            ))}
            <Button
              type="button"
              onClick={addCard}
              className="bg-gray-800 hover:bg-gray-900 text-white text-sm h-9"
            >
              <Plus size={14} className="mr-1.5" /> Add card
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function LandingPageEditorView({ slug, onBack, onSlugChange }: LandingPageEditorViewProps) {
  const [pageId, setPageId] = useState<number | null>(null);
  const [title, setTitle] = useState("");
  // `currentSlug` is the slug we actually talk to the API with. It starts
  // out equal to the `slug` prop, but once a rename is saved it moves to the
  // new value — every request after that (further saves, image/icon
  // uploads) must target the new slug, since the old one no longer resolves
  // to this page server-side.
  const [currentSlug, setCurrentSlug] = useState(slug);
  const [slugValue, setSlugValue] = useState(slug);
  const [slugTouched, setSlugTouched] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [sections, setSections] = useState<SectionDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    adminApi
      .getLandingPage(slug)
      .then((page) => {
        setPageId(page.id);
        setTitle(page.title);
        setCurrentSlug(page.slug);
        setSlugValue(page.slug);
        setSlugTouched(false);
        setIsPublished(page.is_published);
        setSections(withClientIds(page.sections as any));
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  function updateSection(clientId: string, updated: SectionDraft) {
    setSections((prev) => prev.map((s) => (s.clientId === clientId ? updated : s)));
  }

  function removeSection(clientId: string) {
    setSections((prev) => reorder(prev.filter((s) => s.clientId !== clientId)));
  }

  function moveSection(index: number, dir: -1 | 1) {
    setSections((prev) => reorder(move(prev, index, dir)));
  }

  function addSection(type: SectionType) {
    setSections((prev) => [...prev, emptySection(prev.length, type)]);
  }

  async function handleSave() {
    setSaving(true);
    setSaveStatus("idle");
    setSaveError(null);
    try {
      const saved = await adminApi.updateLandingPage(currentSlug, {
        title,
        is_published: isPublished,
        sections,
        slug: slugValue.trim() || currentSlug,
      });
      setCurrentSlug(saved.slug);
      setSlugValue(saved.slug);
      setSlugTouched(false);
      setSections(withClientIds(saved.sections as any));
      setSaveStatus("saved");
      onSlugChange?.(saved.slug);
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (err: any) {
      setSaveStatus("error");
      setSaveError(
        err?.message?.includes("400")
          ? "That slug is already in use — choose a different one."
          : null
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleUploadImage(section: SectionDraft, file: File) {
    if (!section.id) return;
    try {
      const { image_url } = await adminApi.uploadLandingPageSectionImage(currentSlug, section.id, file);
      updateSection(section.clientId, { ...section, image_url });
    } catch {
      alert("Failed to upload image.");
    }
  }

  async function handleUploadStepIcon(section: SectionDraft, step: StepDraft, file: File) {
    if (!step.id) return;
    try {
      const { icon_url } = await adminApi.uploadLandingPageStepIcon(currentSlug, step.id, file);
      updateSection(section.clientId, {
        ...section,
        steps: section.steps.map((s) =>
          s.clientId === step.clientId ? { ...s, icon_url } : s
        ),
      });
    } catch {
      alert("Failed to upload icon.");
    }
  }

  async function handleUploadCardIcon(section: SectionDraft, card: CardDraft, file: File) {
    if (!card.id) return;
    try {
      const { icon_url } = await adminApi.uploadLandingPageCardIcon(currentSlug, card.id, file);
      updateSection(section.clientId, {
        ...section,
        cards: section.cards.map((c) =>
          c.clientId === card.clientId ? { ...c, icon_url } : c
        ),
      });
    } catch {
      alert("Failed to upload icon.");
    }
  }

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto pb-24">
        <div className="px-4 md:px-8 py-6 space-y-4 max-w-3xl">
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-40 w-full rounded-lg" />
          <Skeleton className="h-40 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto pb-24">
        <div className="px-4 md:px-8 py-6 space-y-6 ">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-md px-3 py-1.5 hover:bg-gray-50"
          >
            <ArrowLeft size={14} /> All landing pages
          </button>

          {loadError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Failed to load this page. Please refresh and try again.</AlertDescription>
            </Alert>
          )}

          {saveStatus === "error" && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{saveError ?? "Failed to save changes."}</AlertDescription>
            </Alert>
          )}

          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">Title</label>
                <Input
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (!slugTouched) setSlugValue(slugify(e.target.value));
                  }}
                  placeholder="Page title"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">URL slug</label>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-gray-400 shrink-0">/</span>
                  <Input
                    value={slugValue}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setSlugValue(slugify(e.target.value));
                    }}
                    placeholder="page-slug"
                  />
                </div>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 accent-[#00d9d9]"
              />
              <span className="text-sm text-gray-700">Published (visible to the public)</span>
            </label>
            <p className="text-[11px] text-gray-400">
              Live at /{slugValue || currentSlug}
              {slugValue !== currentSlug && " — renaming takes effect on save"}
            </p>
          </div>

          <div className="space-y-4">
            <SectionHeading>Sections</SectionHeading>
            {sections.length === 0 && (
              <p className="text-sm text-gray-400">No sections yet — add one below.</p>
            )}
            {sections.map((section, i) => (
              <SectionEditor
                key={section.clientId}
                section={section}
                onChange={(updated) => updateSection(section.clientId, updated)}
                onRemove={() => removeSection(section.clientId)}
                onMove={(dir) => moveSection(i, dir)}
                isFirst={i === 0}
                isLast={i === sections.length - 1}
                onUploadImage={(file) => handleUploadImage(section, file)}
                onUploadStepIcon={(step, file) => handleUploadStepIcon(section, step, file)}
                onUploadCardIcon={(card, file) => handleUploadCardIcon(section, card, file)}
              />
            ))}

            <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4">
              <p className="text-xs font-medium text-gray-500 mb-2.5">Add a section</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() => addSection("hero")}
                  className="bg-white border border-gray-300 hover:bg-gray-100 text-gray-800 text-sm h-9 shadow-none"
                >
                  <LayoutTemplate size={14} className="mr-1.5" /> Hero section
                </Button>
                <Button
                  type="button"
                  onClick={() => addSection("steps")}
                  className="bg-white border border-gray-300 hover:bg-gray-100 text-gray-800 text-sm h-9 shadow-none"
                >
                  <ListOrdered size={14} className="mr-1.5" /> Steps section
                </Button>
                <Button
                  type="button"
                  onClick={() => addSection("cards")}
                  className="bg-white border border-gray-300 hover:bg-gray-100 text-gray-800 text-sm h-9 shadow-none"
                >
                  <LayoutGrid size={14} className="mr-1.5" /> Cards section
                </Button>
                <Button
                  type="button"
                  onClick={() => addSection("text")}
                  className="bg-white border border-gray-300 hover:bg-gray-100 text-gray-800 text-sm h-9 shadow-none"
                >
                  <Type size={14} className="mr-1.5" /> Text section
                </Button>
                <Button
                  type="button"
                  onClick={() => addSection("image")}
                  className="bg-white border border-gray-300 hover:bg-gray-100 text-gray-800 text-sm h-9 shadow-none"
                >
                  <ImageIcon size={14} className="mr-1.5" /> Image section
                </Button>
                <Button
                  type="button"
                  onClick={() => addSection("questionnaire")}
                  className="bg-white border border-gray-300 hover:bg-gray-100 text-gray-800 text-sm h-9 shadow-none"
                >
                  <ListChecks size={14} className="mr-1.5" /> Questionnaire section
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 md:left-56 right-0 bg-white border-t border-gray-200 px-4 md:px-8 py-3 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {saveStatus === "saved" ? (
            <span className="text-green-600 flex items-center gap-1.5">
              <Check size={14} /> All changes saved
            </span>
          ) : (
            "Unsaved changes are not kept automatically"
          )}
        </p>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white px-6 disabled:opacity-50"
        >
          {saving ? (
            <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</>
          ) : (
            "Save changes"
          )}
        </Button>
      </div>
    </>
  );
}
